import React, { forwardRef } from "react";
import LoadingSpinner from "../LoadingSpinner/loadingSpinner.js";

/**
 * Standardized Button Component
 * @param {Object} props - Button properties
 * @param {string} props.variant - Button variant: 'primary', 'secondary', 'danger', 'ghost', 'outline'
 * @param {string} props.size - Button size: 'sm', 'md', 'lg'
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {boolean} props.loading - Whether button is in loading state
 * @param {string} props.type - Button type: 'button', 'submit', 'reset'
 * @param {Function} props.onClick - Click handler
 * @param {React.ReactNode} props.children - Button content
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.as - Render as different element (e.g., 'span' for label buttons)
 * @param {Object} props.rest - Other button props
 */
const Button = forwardRef(({
    variant = "primary",
    size = "md",
    disabled = false,
    loading = false,
    type = "button",
    onClick,
    children,
    className = "",
    as,
    ...rest
}, ref) => {
    // Base button classes
    const baseClasses = "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";
    
    // Variant classes
    const variantClasses = {
        primary: "bg-gray-900 dark:bg-[#21262d] text-white dark:text-[#c9d1d9] shadow hover:bg-gray-800 dark:hover:bg-[#30363d] focus-visible:ring-gray-500 dark:focus-visible:ring-gray-400",
        secondary: "bg-white dark:bg-[#161b22] text-gray-700 dark:text-[#c9d1d9] border border-gray-300 dark:border-[#30363d] shadow-sm hover:bg-gray-50 dark:hover:bg-[#1c2128] focus-visible:ring-gray-500 dark:focus-visible:ring-gray-400",
        danger: "bg-white dark:bg-[#161b22] text-red-600 dark:text-red-400 border border-red-300 dark:border-red-800 shadow-sm hover:bg-red-50 dark:hover:bg-red-950/30 focus-visible:ring-red-500 dark:focus-visible:ring-red-400",
        ghost: "bg-transparent text-gray-700 dark:text-[#c9d1d9] hover:bg-gray-100 dark:hover:bg-[#1c2128] focus-visible:ring-gray-500 dark:focus-visible:ring-gray-400",
        outline: "bg-transparent text-gray-700 dark:text-[#c9d1d9] border border-gray-300 dark:border-[#30363d] shadow-sm hover:bg-gray-50 dark:hover:bg-[#1c2128] focus-visible:ring-gray-500 dark:focus-visible:ring-gray-400"
    };
    
    // Size classes
    const sizeClasses = {
        sm: "h-8 rounded-md px-3 text-xs",
        md: "h-9 px-4 py-2",
        lg: "h-10 rounded-md px-8"
    };
    
    const buttonClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;
    
    const Component = as || "button";
    const buttonProps = as === "span" 
        ? { ...rest }
        : { type, onClick, disabled: disabled || loading, ...rest };
    
    return (
        <Component
            ref={as ? undefined : ref}
            {...buttonProps}
            className={buttonClasses}
        >
            {loading && (
                <span className="mr-2">
                    <LoadingSpinner size="small" />
                </span>
            )}
            {children}
        </Component>
    );
});

Button.displayName = "Button";

export default Button;

